'use client';
import { resolveImageAlt } from '@eliadmin/core/src/utils/image-alt-utils';

import type { ProductDisplayData as Product } from '@eliadmin/core/src/api/products/types/product-types';
import { AddToCartButton } from '@eliadmin/core/src/components/product/add-to-cart-button';
import { NotInStockDialog } from '@eliadmin/core/src/components/product/dialog/not-in-stock-dialog';
import { ProductPrice } from '@eliadmin/core/src/components/product/product-price';
import { ProductStock } from '@eliadmin/core/src/components/product/product-stock';
import { Badge } from '@eliadmin/core/src/components/ui/badge';
import { Button } from '@eliadmin/core/src/components/ui/button';
import { Card, CardContent, CardFooter } from '@eliadmin/core/src/components/ui/card';
import { DialogProvider, useDialogStore } from '@eliadmin/core/src/components/ui/dialog';
import { Input } from '@eliadmin/core/src/components/ui/input';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useCategoryStore } from '@eliadmin/core/src/stores/category-store';
import { useGlobalStore } from '@eliadmin/core/src/stores/global-store';
import { VariantsProvider, useVariantsStore } from '@eliadmin/core/src/stores/variant-store';
import {
  getProductFilterVariants,
  getProductImage,
  getProductUrl,
  isSellable,
} from '@eliadmin/core/src/utils/product-utils';
import { cn, getContrastYIQ } from '@eliadmin/core/src/utils/style-utils';
import Image from '@eliadmin/core/src/components/image/image-wrapped';
import Link from '@eliadmin/core/src/components/link';
import { LuMinus, LuPlus } from 'react-icons/lu';
import { HtmlRendererRaw } from '@eliadmin/core/src/components/ui/html-renderer-raw';

export type ProductCardProps = {
  product: Product;
  className?: {
    main?: string;
  };
};

function ProductCardInternal({ product, className }: ProductCardProps) {
  const { t } = useTranslations();
  const templateSettings = useGlobalStore(s => s.templateSettings);
  const filter = useCategoryStore(state => state.filter);
  const selectedValues = useVariantsStore(state => state.selectedValues);

  const filterVariants = getProductFilterVariants(product, filter);
  const image = getProductImage(templateSettings, product, filterVariants);
  const productUrl = getProductUrl(product, selectedValues);

  const settings = useGlobalStore(state => state.templateSettings);
  const selectedVariant = useVariantsStore(state => state.selectedVariant);

  const selectedQuantity = useVariantsStore(state => state.selectedQuantity);

  const setSelectedQuantity = useVariantsStore(state => state.setSelectedQuantity);
  const openDialog = useDialogStore(s => s.open);

  const variants = useVariantsStore(state => state.product.variants);

  const sellable = isSellable(selectedVariant ? selectedVariant : product, selectedQuantity);

  const disabled = !sellable || ((variants?.length || 0) > 0 && !selectedVariant);

  const handleChangeQuantity = (value: number) => {
    if (!value) {
      value = 0;
    }
    const newValue = selectedQuantity + value;
    if (newValue <= 0) {
      setSelectedQuantity(1);
    } else {
      const stock = selectedVariant ?? product;
      const sellableNext = isSellable(stock, newValue);
      if (!sellableNext && stock.quantity > 0) {
        openDialog({
          id: NotInStockDialog.id,
          content: <NotInStockDialog maxQuantity={stock.quantity} unit={product.unit} />,
        });
        setSelectedQuantity(stock.quantity);
      } else if (sellableNext) {
        setSelectedQuantity(newValue);
      }
    }
  };

  return (
    <Card
      className={cn(
        // shadow-[0_-2px_60px_-15px_rgba(0,0,0,0.15)] removed, kept it there because of nice regex pattern
        'relative flex h-full min-w-[250px] flex-col gap-2 rounded-none border border-border',
        'ea-product-card',
        className?.main,
      )}
    >
      <div
        className={cn(
          'absolute left-4 top-4 z-10 flex flex-col flex-wrap items-center gap-1 text-xs',
          'ea-product-card__badges',
        )}
      >
        {product.tags?.map((badge, i) => (
          <Badge
            key={`badge-${badge.name ?? i}`}
            style={{ backgroundColor: badge.color, color: getContrastYIQ(badge.color) }}
            size="sm"
            className={cn('ea-product-card__badge')}
          >
            {badge.name}
          </Badge>
        ))}
      </div>

      <CardContent
        className={cn('flex flex-1 flex-col gap-2 p-3 pb-0', 'ea-product-card__content')}
      >
        {image?.url && (
          <Link prefetchOnHover href={productUrl} className="" prefetch={false}>
            <Image
              alt={resolveImageAlt(image, product?.name)}
              src={image.url}
              width="0"
              height="0"
              sizes="100vw"
              className="aspect-square w-full rounded-lg object-contain"
            />
          </Link>
        )}

        <Link
          prefetchOnHover
          href={productUrl}
          className={cn('border-t pt-3 text-center text-lg font-bold', 'ea-product-card__name')}
          prefetch={false}
        >
          {product.name}
        </Link>

        {templateSettings.product_teaser.short_description.enabled && (
          <HtmlRendererRaw
            className={cn(
              'line-clamp-4 h-20 overflow-hidden text-ellipsis text-center text-sm font-normal text-muted-foreground',
              'ea-product-card__short-description',
            )}
            html={product.short_description}
          />
        )}
      </CardContent>

      {/* footer */}
      <CardFooter className={cn('flex justify-end gap-2 p-3 pt-0', 'ea-product-card__footer')}>
        <div className="mb-[6px] flex w-full flex-col items-center justify-between gap-3">
          {/* Price */}
          {/* <Price product={product} filterVariants={filterVariants} /> */}
          <ProductPrice
            product={product}
            className={{
              main: cn('items-center gap-0', 'ea-product-card__product-price'),
              discount:
                'w-15 absolute right-0 top-0 flex min-h-10 flex-col flex-wrap items-center justify-center rounded-none bg-red-500 py-1 text-white shadow-none',
              priceAfterDiscount: 'text-[18px]',
            }}
          />

          <div className="mb-1 flex rounded-[4px] border border-border">
            <Button
              aria-label={`${t('text22')} −: ${product.name}`}
              variant="outline"
              className={cn(
                'h-10 w-10 rounded-none border-none bg-border p-4',
                'ea-product-detail-quantity__substract-button ea-product-detail-quantity__button',
              )}
              disabled={selectedQuantity <= 1}
              onClick={() => {
                handleChangeQuantity(-1);
              }}
            >
              <LuMinus size={16} strokeWidth={3} color="black" />
            </Button>
            <div className="h-10 w-10 flex-grow justify-center">
              <Input
                aria-label={`${t('text22')}: ${product.name}`}
                className={{
                  wrapper:
                    'flex-grow-1 h-10 w-full rounded-none bg-white sm:w-10 sm:max-w-[48px] sm:flex-grow-0',
                  main: 'flex-grow-1 h-10 w-full text-center sm:w-10 sm:max-w-[48px] ',
                }}
                value={selectedQuantity}
                onChange={e => {
                  const value = parseInt(e.target.value);
                  if (value) {
                    handleChangeQuantity(value - selectedQuantity);
                  }
                }}
                onFocus={e => {
                  e.target.select();
                }}
              />
            </div>

            <Button
              aria-label={`${t('text22')} +: ${product.name}`}
              variant="outline"
              className={cn(
                'h-10 w-10 rounded-none border-none bg-border p-4',
                'ea-product-detail-quantity__increase-button ea-product-detail-quantity__button',
              )}
              onClick={() => {
                handleChangeQuantity(1);
              }}
            >
              <LuPlus size={16} strokeWidth={3} color="black" />
            </Button>
          </div>
        </div>
        <div
          className={cn(
            'flex w-full flex-col items-center justify-between gap-3',
            'ea-product-card__footer-top',
          )}
        >
          {templateSettings.product_teaser.availability.enabled && (
            <ProductStock
              className={cn(
                'flex w-full flex-row items-center justify-center gap-x-2 whitespace-nowrap rounded-none shadow-none',
                'ea-product-cart__stock',
              )}
            />
          )}
          {/* add to cart */}
          <AddToCartButton
            className={cn(
              'h-12 w-full flex-grow rounded-[4px] sm:max-w-[250px]',
              'ea-product-detail-quantity__add-to-cart-button',
            )}
            quantity={selectedQuantity}
            disabled={disabled}
            product={product}
            sellable={true}
            showIcon={false}
          />
        </div>
      </CardFooter>
    </Card>
  );
}

export function ProductCard(props: ProductCardProps) {
  return (
    <VariantsProvider
      key={`product-${props.product.id ?? props.product.slug}`}
      state={{ product: props.product }}
    >
      <DialogProvider>
        <ProductCardInternal {...props} />
      </DialogProvider>
    </VariantsProvider>
  );
}
